import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';
import { Link } from 'react-router-dom';
import Hero from '../sections/Hero.jsx';
import Clients from '../sections/Clients.jsx';

// pages/Home.jsx — Landing page (Hero + studio explorer + featured + CTA)
const PageHome = (() => {
  const { Heart, Coin, Star, Diamond, Joystick, Bolt, Book, Cart, Mushroom, Crown, Arrow } = PixelIcons;

  const worlds = [
    { to: "/estudio",   k: "studio",    Icon: Crown,    accent: "var(--ragross-cyan)",    code: "W-02" },
    { to: "/servicios", k: "services",  Icon: Bolt,     accent: "var(--ragross-magenta)", code: "W-03" },
    { to: "/portafolio",k: "portfolio", Icon: Joystick, accent: "var(--ragross-gold)",    code: "W-04" },
    { to: "/talleres",  k: "workshops", Icon: Star,     accent: "var(--ragross-green)",   code: "W-06" },
    { to: "/tienda",    k: "shop",      Icon: Cart,     accent: "var(--ragross-magenta)", code: "W-07" },
    { to: "/blog",      k: "blog",      Icon: Book,     accent: "var(--ragross-cyan)",    code: "W-08" },
    { to: "/contacto",  k: "contact",   Icon: Heart,    accent: "var(--ragross-magenta)", code: "W-09" },
  ];

  // Reuse a few featured projects
  const featured = [
    { code: "#01", img: "/assets/portfolio/pacific-travel-videojuego.jpg", cat: "videojuego", stack: "UNITY · WebGL",     name: "Pacific Travel", tag: "FEATURED", tagColor: "var(--ragross-gold)" },
    { code: "#03", img: "/assets/portfolio/ymca-home.png",                 cat: "web",         stack: "INSTITUCIONAL",    name: "YMCA Cali",      tag: "LIVE",     tagColor: "var(--ragross-cyan)" },
    { code: "#08", img: "/assets/portfolio/maefloresta.png",               cat: "multimedia",  stack: "AR · EDUCATIVO",   name: "Maefloresta",    tag: "PLAY NOW", tagColor: "var(--ragross-green)" },
  ];

  const exStyles = {
    section: { padding: "112px 0", borderTop: "1px solid var(--border-soft)" },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, marginBottom: 56 },
    tag: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 11, letterSpacing: "0.18em",
      color: "var(--ragross-magenta)",
    },
    title: { font: "var(--type-display-lg)", color: "#fff", margin: 0, lineHeight: 1.06, letterSpacing: "-0.015em" },
    sub: { font: "var(--type-body-lg)", color: "var(--fg-2)", margin: 0, maxWidth: 580 },
    grid: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 14 },
    card: {
      position: "relative",
      flex: "1 1 240px",
      maxWidth: 300,
      background: "var(--ragross-purple-night)",
      border: "2px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      padding: 22,
      display: "flex", flexDirection: "column", gap: 16,
      minHeight: 200,
      textDecoration: "none",
      color: "inherit",
      transition: "transform 200ms var(--ease-arcade), border-color 200ms, box-shadow 200ms",
      overflow: "hidden",
    },
    cardScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)", opacity: 0.35, pointerEvents: "none",
    },
    code: {
      position: "absolute", top: 10, right: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.14em",
      color: "var(--fg-3)",
    },
    ico: (color) => ({
      width: 54, height: 54,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--ragross-purple-deep)",
      border: `2px solid ${color}`,
      boxShadow: `4px 4px 0 ${color}`,
      position: "relative", zIndex: 1,
    }),
    name: {
      font: "var(--type-h2)", color: "#fff", margin: 0,
      position: "relative", zIndex: 1,
    },
    desc: {
      font: "var(--type-body-sm)", color: "var(--fg-2)", margin: 0, flex: 1,
      position: "relative", zIndex: 1,
    },
    cta: {
      display: "flex", alignItems: "center", gap: 8,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 10, letterSpacing: "0.16em",
      paddingTop: 10, borderTop: "1px solid var(--border-soft)",
      position: "relative", zIndex: 1,
    },
  };

  function WorldCard({ w }) {
    const t = useT();
    const [hover, setHover] = React.useState(false);
    const Icon = w.Icon;
    return (
      <Link
        to={w.to}
        style={{
          ...exStyles.card,
          borderColor: hover ? w.accent : exStyles.card.borderColor,
          transform: hover ? "translate(-3px,-3px)" : "none",
          boxShadow: hover ? `6px 6px 0 ${w.accent}` : "none",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div style={exStyles.cardScan}></div>
        <span style={exStyles.code}>{w.code}</span>
        <div style={exStyles.ico(w.accent)}>
          <Icon size={26}/>
        </div>
        <h3 style={exStyles.name}>{t(`page.${w.k}.title`)}</h3>
        <p style={exStyles.desc}>{t(`page.${w.k}.sub`)}</p>
        <div style={{ ...exStyles.cta, color: w.accent }}>
          {t("home.explore.studioCta")}
          <Arrow size={11} color={w.accent}/>
        </div>
      </Link>
    );
  }

  // ------- Featured strip (3 cartridges + view-all CTA)
  const ftStyles = {
    section: { padding: "112px 0", background:
      "linear-gradient(180deg, transparent 0%, rgba(57,34,110,0.12) 100%), var(--bg-app)",
      borderTop: "1px solid var(--border-soft)",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 36 },
    tag: { fontFamily: "'Press Start 2P', monospace", fontSize: 11, letterSpacing: "0.18em", color: "var(--ragross-magenta)" },
    title: { font: "var(--type-display-md)", color: "#fff", margin: 0, marginTop: 10, lineHeight: 1.1 },
    grid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 },
    cart: {
      background: "var(--ragross-purple-deep)",
      border: "2px solid var(--ragross-magenta)",
      borderRadius: "var(--radius-card)",
      overflow: "hidden",
      boxShadow: "4px 4px 0 0 var(--ragross-cyan)",
      transition: "transform 200ms var(--ease-arcade), box-shadow 200ms",
      cursor: "pointer", display: "flex", flexDirection: "column",
    },
    screen: { position: "relative", aspectRatio: "16/10", overflow: "hidden", background: "var(--bg-screen)" },
    screenImg: { width: "100%", height: "100%", objectFit: "cover", filter: "saturate(0.92) contrast(1.02)" },
    screenScan: { position: "absolute", inset: 0, background: "var(--scanlines)", opacity: 0.5, pointerEvents: "none" },
    screenTag: {
      position: "absolute", top: 8, left: 8,
      padding: "4px 7px", fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.10em",
      background: "rgba(11,0,20,0.85)", borderRadius: 2,
    },
    screenCode: {
      position: "absolute", top: 8, right: 8,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9, letterSpacing: "0.10em",
      color: "var(--ragross-gold)",
    },
    label: {
      padding: "6px 10px",
      background: "linear-gradient(90deg, var(--ragross-magenta), var(--ragross-purple-2))",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.12em",
      color: "#fff", textAlign: "center",
      borderTop: "1px solid rgba(255,255,255,0.15)",
      borderBottom: "1px solid rgba(0,0,0,0.4)",
    },
    meta: { padding: 14, display: "flex", flexDirection: "column", gap: 6 },
    stack: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.10em",
      color: "var(--ragross-cyan)",
    },
    name: { font: "var(--type-h3)", color: "#fff", margin: 0 },
  };

  function FeaturedCart({ p }) {
    const [h, setH] = React.useState(false);
    return (
      <Link
        to="/portafolio"
        style={{
          ...ftStyles.cart,
          transform: h ? "translate(-2px,-2px)" : "none",
          boxShadow: h ? "6px 6px 0 0 var(--ragross-cyan), 0 0 40px rgba(253,20,145,0.4)" : ftStyles.cart.boxShadow,
        }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
      >
        <div style={ftStyles.screen}>
          <img src={p.img} alt={p.name} style={ftStyles.screenImg}/>
          <div style={ftStyles.screenScan}></div>
          <span style={{ ...ftStyles.screenTag, color: p.tagColor, border: `1px solid ${p.tagColor}` }}>▸ {p.tag}</span>
          <span style={ftStyles.screenCode}>{p.code}</span>
        </div>
        <div style={ftStyles.label}>RAGROSS · {p.code} · {p.cat.toUpperCase()}</div>
        <div style={ftStyles.meta}>
          <span style={ftStyles.stack}>{p.stack}</span>
          <h3 style={ftStyles.name}>{p.name}</h3>
        </div>
      </Link>
    );
  }

  // ------- Home CTA strip
  const ctaStyles = {
    section: {
      padding: "96px 0",
      position: "relative",
      overflow: "hidden",
      background: "var(--ragross-purple-night)",
      borderTop: "1px solid var(--border-soft)",
    },
    bg: {
      position: "absolute", inset: 0,
      background:
        "radial-gradient(circle at 20% 30%, rgba(253,20,145,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,232,203,0.20), transparent 50%)",
      pointerEvents: "none",
    },
    bgScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)", opacity: 0.4, pointerEvents: "none",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px", position: "relative", zIndex: 2,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 18, textAlign: "center" },
    tag: { fontFamily: "'Press Start 2P', monospace", fontSize: 12, letterSpacing: "0.20em", color: "var(--ragross-gold)" },
    title: { font: "var(--type-display-md)", color: "#fff", margin: 0, maxWidth: 800, lineHeight: 1.1 },
    sub: { font: "var(--type-body-lg)", color: "var(--fg-2)", margin: 0, maxWidth: 640 },
    ctas: { display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", marginTop: 12 },
  };

  function PageHome() {
    const t = useT();
    return (
      <>
        <Hero/>

        <Clients/>

        {/* Studio explorer */}
        <section style={exStyles.section} id="explore" data-screen-label="01b Explore">
          <div style={exStyles.container}>
            <div style={exStyles.head}>
              <div style={exStyles.tag}>{t("home.explore.tag")}</div>
              <h2 style={exStyles.title} className="section-title">{t("home.explore.title")}</h2>
              <p style={exStyles.sub}>{t("home.explore.sub")}</p>
            </div>
            <div style={exStyles.grid}>
              {worlds.map((w) => <WorldCard key={w.k} w={w}/>)}
            </div>
          </div>
        </section>

        {/* Featured projects */}
        <section style={ftStyles.section} data-screen-label="01c Featured">
          <div style={ftStyles.container}>
            <div style={ftStyles.head}>
              <div>
                <div style={ftStyles.tag}>{t("home.featured.tag")}</div>
                <h2 style={ftStyles.title} className="section-title">{t("home.featured.title")}</h2>
              </div>
              <Link to="/portafolio" className="btn btn-secondary">
                <span>{t("home.featured.viewAll")}</span>
                <Arrow size={11} color="var(--ragross-cyan)"/>
              </Link>
            </div>
            <div style={ftStyles.grid} className="responsive-grid-3">
              {featured.map((p) => <FeaturedCart key={p.code} p={p}/>)}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section style={ctaStyles.section} data-screen-label="01d CTA">
          <div style={ctaStyles.bg}></div>
          <div style={ctaStyles.bgScan}></div>
          <div style={ctaStyles.container}>
            <div style={ctaStyles.tag}>{t("home.cta.tag")}</div>
            <h2 style={ctaStyles.title} className="section-title">{t("home.cta.title")}</h2>
            <p style={ctaStyles.sub}>{t("home.cta.sub")}</p>
            <div style={ctaStyles.ctas}>
              <Link to="/contacto" className="btn btn-primary">
                <Heart size={12}/>
                <span>{t("home.cta.primary")}</span>
              </Link>
              <Link to="/portafolio" className="btn btn-secondary">
                <span>{t("home.cta.secondary")}</span>
                <Arrow size={11} color="var(--ragross-cyan)"/>
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return PageHome;
})();

export default PageHome;
